import clsx from 'clsx';
import { isEmpty } from 'lodash';
import React, { useEffect, useRef } from 'react';

import BlueTriangleIcon from 'images/enterprise_powerlist/triangle.png';
import CloseIcon from 'images/enterprise_powerlist/x.svg';

import { PowerlistPersonDetails } from '../EnterprisePowerlist/Layout';
import styles from './styles.module.css';

enum PersonType {
  Investor = 'investor',
  Operator = 'operator',
  EmergingOperator = 'emerging-operator',
  EmergingInvestor = 'emerging-investor',
}

interface OverviewDetails {
  title: string;
  text: string;
}

const IMGIX_BASE_URL = 'https://tii.imgix.net/production/enterprise-power-list/dropdown';

const DetailsRow = ({
  details,
  handleClose,
}: {
  details: PowerlistPersonDetails;
  handleClose: () => void;
}) => {
  const detailsRowRef = useRef(null);

  useEffect(() => {
    if (detailsRowRef.current) {
      detailsRowRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [details]);

  const hasRelatedArticles = details.article_url && details.article_title;

  // article_title and article_url are ';' separated lists
  let articleTitles = [];
  let articleUrls = [];
  if (hasRelatedArticles) {
    articleTitles = details.article_title.split(';');
    articleUrls = details.article_url.split(';');
  }

  // Render slightly different copy depending on whether person is an operator or investor
  const operatorDetails: OverviewDetails[] = [
    {
      title: 'Company',
      text: details.firm,
    },
    {
      title: 'Valuation',
      text: details.value,
    },
    {
      title: 'Extra Credit',
      text: details.extra_credit,
    },
  ];

  const emergingOperatorDetails: OverviewDetails[] = [
    {
      title: 'Company',
      text: details.firm,
    },
    {
      title: 'Valuation',
      text: details.value,
    },
  ];

  const investorDetails: OverviewDetails[] = [
    {
      title: 'Firm',
      text: details.firm,
    },
    {
      title: 'Key Exits',
      text: details.key_exits,
    },
    {
      title: 'Key Active Investments',
      text: details.key_active_investments,
    },
  ];

  const emergingInvestorDetails: OverviewDetails[] = [
    {
      title: 'Firm',
      text: details.firm,
    },
    {
      title: 'Key Active Investments',
      text: details.key_active_investments,
    },
  ];

  const personDetails = {
    [PersonType.Operator]: operatorDetails,
    [PersonType.Investor]: investorDetails,
    [PersonType.EmergingOperator]: emergingOperatorDetails,
    [PersonType.EmergingInvestor]: emergingInvestorDetails,
  };

  return (
    <div className="details-row mb3" ref={detailsRowRef}>
      <div className="rank enterprise-rank">{details.rank}</div>
      <div className="details-portrait-container">
        <div className={clsx(styles.detailsText, 'details-name')}>{details.name}</div>
        <div className="details-image">
          <img
            height={250}
            width={250}
            src={`${IMGIX_BASE_URL}/${details.photo}?fmt=webp&w=250&h=250&fit=crop`}
          />
          <div className="details-source">{details.source}</div>
        </div>
      </div>
      <div className="description-container">
        {!isEmpty(personDetails[details.type]) &&
          personDetails[details.type].map((overview) => (
            <div key={overview.title} className={clsx(styles.detailsText, 'overview')}>
              <div className={styles.textTitle}>{overview.title}</div>
              <div>{overview.text}</div>
            </div>
          ))}
        <div className={styles.descriptionText}>{details.description}</div>
        {hasRelatedArticles &&
          articleTitles.map((title, idx) => (
            <div key={title} className={styles.textTitle}>
              Related:{' '}
              <a className={styles.textTitle} href={articleUrls[idx]}>
                &ldquo;{title}&rdquo;
              </a>
            </div>
          ))}
        {details.notes && <div className="note-text">{details.notes}</div>}
      </div>
      <img className="triangle" src={BlueTriangleIcon} alt="triangle" />
      <input
        className="close-button"
        onClick={handleClose}
        type="image"
        name="closeButton"
        src={CloseIcon}
        alt="close"
      />
    </div>
  );
};

export default DetailsRow;
